import {
  PRODUCT_ATTR_DISPLAY_MODE,
  PRODUCT_SHIP_TYPE,
  PROPOSAL_REVIEW_STATUS,
  PROPOSAL_TYPE
} from '@vzmi/shp_shared_compo/dist/constants/proposal.js'
import DOMPurify from 'isomorphic-dompurify'
import Fuse from 'fuse.js'
import _ from 'lodash'
import moment from 'moment'
import appConf from '../../../../../../conf/appConf'
import {
  CAUSE_CODE_MAP,
  CHECKLIST_EMPTY_LABEL,
  CHECKLIST_DISPLAY_ITEM_SEPARATOR,
  CHECKLIST_DISPLAY_LABEL_VALUE_SEPARATOR,
  CHECKLIST_DISPLAY_SPEC_VALUES_SEPARATOR,
  FIELD,
  PRODUCT_IMAGE_HASH,
  SCHEMA
} from './constants'

const SAFE_COPY_ALLOWED_TAGS = [
  'div',
  'span',
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'a',
  'img',
  'iframe',
  'picture',
  'source'
]

const SAFE_COPY_ALLOWED_ATTR = [
  'src',
  'srcset',
  'alt',
  'width',
  'height',
  'frameborder',
  'allow',
  'allowfullscreen',
  'class',
  'style'
]

const YOUTUBE_EMBED_SRC_REGEX = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com)\/embed\//i

/** Allow only https (no http) for URI attributes (href, src). Allow digits for non-URI attrs (e.g. width, height). */
const ALLOWED_URI_REGEXP = /^(https:\/\/[^\s]*|\d+)$/i

/** Full-string match: NCC + optional space + 認證碼 / 認証碼 */
const NCC_CERTIFICATION_LABEL_REGEX = /^NCC\s?認[證証]碼$/u

const BSMI_LABEL_SUBSTRINGS = [
  '認證號碼',
  '商檢局認證',
  '商品檢驗識別號碼 (請輸入號碼)',
  '商檢字號'
]

const TD_CERTIFICATION_LABEL_SUBSTRING = 'TD識別碼'

/**
 * Returns label with external prefix (E). No category/match logic.
 * @param {string} label - Attribute or spec name
 * @returns {string} `${label}(E)`
 */
export const getExternalLabel = (label) => `${label}(E)`

/**
 * True when the label matches regulatory certification patterns (NCC full-label regex, BSMI text
 * or listed BSMI-related phrases, or TD識別碼). Such rows are always marked external.
 * @param {unknown} label
 * @returns {boolean}
 */
export const isRegulatoryCertificationAttributeLabel = (label) => {
  if (typeof label !== 'string' || !label) {
    return false
  }
  if (NCC_CERTIFICATION_LABEL_REGEX.test(label)) {
    return true
  }
  if (label.toUpperCase().includes('BSMI')) {
    return true
  }
  if (BSMI_LABEL_SUBSTRINGS.some((s) => label.includes(s))) {
    return true
  }
  return label.includes(TD_CERTIFICATION_LABEL_SUBSTRING)
}

/**
 * Returns the first URL from a srcset string (e.g. "url1 1x, url2 2x" -> "url1").
 */
export const getFirstUrlFromSrcset = (srcset) => {
  if (typeof srcset !== 'string' || !srcset.trim()) {
    return ''
  }

  const first = srcset.split(',')[0].trim()
  const url = first.split(/\s+/)[0]
  return url || ''
}

/**
 * Ensures URL has https?:// prefix; otherwise prepends https://
 * @param {string} url - Raw URL (may omit protocol)
 * @returns {string}
 */
export const getNormalizedUrl = (url) => {
  return !/^https?:\/\//i.test(url) ? `https://${url}` : url
}

/**
 * Appends product image hash to URL to mark it as a product image.
 * @param {string} url - Image URL
 * @returns {string} url + PRODUCT_IMAGE_HASH, or '' if url is not a string
 */
export const appendProductImageHash = (url) => {
  if (_.isEmpty(url)) {
    return ''
  }

  return `${url}${PRODUCT_IMAGE_HASH}`
}

/**
 * Returns true if url is marked as a product image (ends with PRODUCT_IMAGE_HASH).
 * @param {string} url - URL (may include hash)
 * @returns {boolean}
 */
export const isProductImageUrl = (url = '') => {
  return `${url}`.endsWith(PRODUCT_IMAGE_HASH)
}

/**
 * Removes product image hash from URL.
 * @param {string} url - URL (may include hash)
 * @returns {string} URL without hash, or url unchanged if not a string
 */
export const stripProductImageHash = (url = '') => {
  if (_.isEmpty(url)) {
    return ''
  }

  return `${url}`.replace(PRODUCT_IMAGE_HASH, '')
}

/**
 * Returns a display string for a proposal/schema field value (used in checklist UI).
 * @param {string} path - Schema path (e.g. SCHEMA.TITLE, SCHEMA.PRODUCT_IMAGES)
 * @param {*} value - Raw value at that path
 * @returns {string|number} Display string or count for PRODUCT_IMAGES
 */
export const getProposalFieldDisplayValue = (path, value) => {
  if (value === null || value === undefined) {
    return CHECKLIST_EMPTY_LABEL
  }

  if (path === SCHEMA.PRODUCT_IMAGES) {
    return _.size(value)
  }

  if (path === SCHEMA.PRODUCT_COPY) {
    if (_.isEmpty(value)) {
      return CHECKLIST_EMPTY_LABEL
    }

    return '有資料'
  }

  if (path === SCHEMA.ATTRIBUTES_TABLE) {
    if (_.isEmpty(value)) {
      return CHECKLIST_EMPTY_LABEL
    }

    return _.flow(
      () => _.map(value, ({ label, value }) => [label, value].join(CHECKLIST_DISPLAY_LABEL_VALUE_SEPARATOR)),
      (arr) => arr.join(CHECKLIST_DISPLAY_ITEM_SEPARATOR)
    )()
  }

  if (path === SCHEMA.PRODUCT_SPECS) {
    if (_.isEmpty(value)) {
      return CHECKLIST_EMPTY_LABEL
    }

    return _.flow(
      () => _.map(value, ({ name, values }) => {
        return [name, _.join(values, CHECKLIST_DISPLAY_SPEC_VALUES_SEPARATOR)].join(CHECKLIST_DISPLAY_LABEL_VALUE_SEPARATOR)
      }),
      (arr) => arr.join(CHECKLIST_DISPLAY_ITEM_SEPARATOR)
    )()
  }

  if (path === SCHEMA.BRAND) {
    if (_.isEmpty(value)) {
      return CHECKLIST_EMPTY_LABEL
    }

    return _.get(value, 'name', CHECKLIST_EMPTY_LABEL)
  }

  if (Array.isArray(value)) {
    if (_.isEmpty(value)) {
      return CHECKLIST_EMPTY_LABEL
    }

    return value.join(CHECKLIST_DISPLAY_ITEM_SEPARATOR)
  }

  const str = String(value).trim()
  if (_.isEmpty(str)) {
    return CHECKLIST_EMPTY_LABEL
  }

  return str
}

/** 移除非中英文、數字、連字符號、空格的字符 (CJK U+4E00–U+9FFF, U+3400–U+4DBF, a-zA-Z0-9, hyphen, space) */
const NON_CJK_ALPHANUMERIC_REGEX = /[^\u4e00-\u9fff\u3400-\u4dbfa-zA-Z0-9-\s]/g

/**
 * Strips characters that are not CJK, ASCII letters, digits, hyphen, or space.
 * @param {string} str - Input string
 * @returns {string} Sanitized string, or '' if str is not a string
 */
export const stripNonCjkAlphanumeric = (str) => {
  if (typeof str !== 'string') return ''
  return str.replace(NON_CJK_ALPHANUMERIC_REGEX, '')
}

/**
 * Extracts image extension from URL path (png, jpeg, jpg, gif, webp). Returns 'png' on invalid URL or no match.
 * @param {string} url - Image URL
 * @returns {string}
 */
export const getExtensionFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname
    const match = pathname.match(/\.(png|jpe?g|gif|webp)(?:\?|$)/i)
    return match ? match[1].toLowerCase() : 'png'
  } catch {
    return 'png'
  }
}

/**
 * Returns true if the category attribute is text type or its values include "其他" or the input's first value.
 * Returns false when categoryAttributes is empty (e.g. API failed or no data).
 * @param {Array<{ name: string, constraints?: Object, values?: Array }>} categoryAttributes - Array of category attribute objects
 * @param {{ label: string, value?: string, values?: array }} attribute - Attribute to check (label + value/values)
 * @param {boolean} [isSpec=false]
 * @returns {boolean}
 */
export const matchAttribute = (categoryAttributes, attribute, isSpec = false) => {
  if (!attribute || !_.get(attribute, 'label')) {
    return false
  }

  if (_.isEmpty(categoryAttributes)) {
    return false
  }

  const attrsByName = _.keyBy(categoryAttributes, 'name')
  const attr = _.get(attrsByName, attribute.label, {})
  if (_.isEmpty(attr)) {
    return true
  }

  if (_.get(attr, 'constraints.type') === 'text') {
    return true
  }

  const valuesArr = _.get(attr, 'values', [])
  const valuesMap = _.keyBy(valuesArr)
  const firstVal = _.get(attribute, 'value')
  if (!isSpec && '其他' in valuesMap) {
    return true
  }

  if (firstVal in valuesMap) {
    return true
  }

  return false
}

/** Default brand id when no match (e.g. "其他"). */
const DEFAULT_BRAND_ID = '41171558'
/** Fuse.js threshold: 0 = exact, 1 = match anything. Allow minor typos/variations. */
const FUSE_THRESHOLD = 0.4

/**
 * Maps API result payload.brands to normalized list { id, name, upperName }.
 * @param {Object} result - API result with payload.brands
 * @returns {Array<{ id: string, name: string, upperName: string }>}
 */
export const normalizeBrandsFromPayload = (result) => {
  const list = _.get(result, 'payload.brands', [])
  return _.map(list, (b) => ({
    id: b.id,
    name: b.name,
    upperName: (b.name || '').replace(/[^\w\s]/g, '').trim().toUpperCase()
  }))
}

/**
 * Find brand id by name (exact or fuzzy match on upperName).
 * Prefers exact match, then uses Fuse.js for fuzzy matching (typos, partial phrases).
 * @param {Array<{ id: string, name: string, upperName: string }>} brands - Normalized brand list
 * @param {string} name - Brand name from external page (may be partial or noisy)
 * @returns {string} Brand id or DEFAULT_BRAND_ID
 */
export const resolveBrandId = (brands, name) => {
  const inputUpper = (name || '').replace(/[^\w\s]/g, '').trim().toUpperCase()
  if (!inputUpper || _.isEmpty(brands)) {
    return DEFAULT_BRAND_ID
  }

  const exact = _.find(brands, (b) => b.upperName === inputUpper)
  if (exact) {
    return _.get(exact, 'id', DEFAULT_BRAND_ID)
  }

  const fuse = new Fuse(brands, {
    keys: ['upperName'],
    threshold: FUSE_THRESHOLD,
    includeScore: true
  })
  const results = fuse.search(inputUpper)
  if (_.isEmpty(results)) {
    return DEFAULT_BRAND_ID
  }

  return _.get(results[0], 'item.id', DEFAULT_BRAND_ID)
}

const ALLOWED_CDN_PREFIX = appConf.cdn + '/'
/**
 * Sanitizes HTML for safe storage and display (e.g. product copy).
 * Allows only safe content tags, YouTube embed iframes (no onload/events), and images (https only).
 * Links (<a>) are replaced with <span> preserving only the class attribute.
 * @param {string} html - Raw HTML string (e.g. from Momo goods iframe)
 * @param {boolean} [isCleanup=false] - When true, remove img elements whose src does not start with CDN prefix (appConf.cdn)
 * @returns {string} Sanitized HTML or '' for non-string/empty input
 */
export const getSafeCopy = (html, isCleanup = false) => {
  if (typeof html !== 'string') {
    return ''
  }
  const trimmed = html.trim()
  if (trimmed === '') {
    return ''
  }

  const replaceAnchorWithSpan = (node, data) => {
    if (data.tagName === 'a') {
      const span = node.ownerDocument.createElement('span')
      const cls = node.getAttribute('class')
      if (cls) {
        span.setAttribute('class', cls)
      }
      while (node.firstChild) {
        span.appendChild(node.firstChild)
      }
      if (node.parentNode) {
        node.parentNode.replaceChild(span, node)
      }
    }
  }

  const allowOnlyYoutubeIframe = (node, data) => {
    if (data.tagName === 'iframe') {
      const src = node.getAttribute('src') || ''
      if (!YOUTUBE_EMBED_SRC_REGEX.test(src)) {
        if (node.parentNode) {
          node.parentNode.removeChild(node)
        }
      } else {
        const currentStyle = (node.getAttribute('style') || '').trim()
        if (!currentStyle) {
          node.setAttribute('style', 'width: 100%;')
        } else if (!/width\s*:/i.test(currentStyle)) {
          const separator = currentStyle.endsWith(';') ? '' : '; '
          node.setAttribute('style', currentStyle + separator + 'width: 100%;')
        }
      }
    }
  }

  const normalizeProtocolRelativeImgSrc = (node, data) => {
    if (data.attrName === 'src' && node.tagName === 'IMG') {
      const val = data.attrValue
      if (typeof val === 'string' && val.startsWith('//')) {
        data.attrValue = 'https:' + val
      }
    }
  }

  /** Strip src and href when value is not https (no http, mailto, or data:). */
  const allowOnlyHttpsUri = (node, data) => {
    if ((data.attrName === 'src' || data.attrName === 'href') && typeof data.attrValue === 'string') {
      if (!/^https:\/\//i.test(data.attrValue)) {
        data.attrValue = ''
      }
    }
  }

  /** Removes img elements that have no src or empty src. */
  const removeEmptySrcImg = (node, data) => {
    if (data.tagName === 'img') {
      const src = node.getAttribute('src')
      if (src == null || (typeof src === 'string' && src.trim() === '')) {
        if (node.parentNode) {
          node.parentNode.removeChild(node)
        }
      }
    }
  }

  /**
   * Replaces <picture> with <img>: uses first <source> srcset or inner <img> srcset/src, and width/height if present.
   * When srcset is used, only the first image URL is set as img src.
   */
  const replacePictureWithImg = (node, data) => {
    if (data.tagName !== 'picture') {
      return
    }
    const firstSource = node.querySelector('source')
    const imgChild = node.querySelector('img')
    const srcset = firstSource?.getAttribute('srcset') || imgChild?.getAttribute('srcset')
    const src = imgChild?.getAttribute('src') || firstSource?.getAttribute('src')
    const width = node.getAttribute('width') || imgChild?.getAttribute('width')
    const height = node.getAttribute('height') || imgChild?.getAttribute('height')
    const alt = imgChild?.getAttribute('alt') || ''
    const img = node.ownerDocument.createElement('img')
    const source = srcset ? getFirstUrlFromSrcset(srcset) : src
    if (source) {
      img.setAttribute('src', source)
    }
    if (width && height) {
      img.setAttribute('width', width)
      img.setAttribute('height', height)
    }
    if (alt) {
      img.setAttribute('alt', alt)
    }
    if (node.parentNode) {
      node.parentNode.replaceChild(img, node)
    }
  }

  /** When isCleanup is true, remove img elements whose src does not start with CDN prefix. */
  const removeNonCdnImg = (node, data) => {
    if (!isCleanup || data.tagName !== 'img') {
      return
    }
    const src = node.getAttribute('src')
    if (src == null || typeof src !== 'string' || !src.startsWith(ALLOWED_CDN_PREFIX)) {
      if (node.parentNode) {
        node.parentNode.removeChild(node)
      }
    }
  }

  /** Remove position and padding-bottom from div inline style (e.g. aspect-ratio wrappers). */
  const cleanDivStyle = (node, data) => {
    if (data.tagName !== 'div') {
      return
    }
    const style = node.getAttribute('style')
    if (style == null || typeof style !== 'string') {
      return
    }
    const toStrip = ['position', 'padding-bottom']
    const kept = style
      .split(';')
      .map((decl) => decl.trim())
      .filter((decl) => {
        if (!decl) return false
        const colon = decl.indexOf(':')
        const prop = (colon >= 0 ? decl.slice(0, colon).trim() : decl).toLowerCase()
        return !toStrip.includes(prop)
      })
    const newStyle = kept.join('; ').trim()
    if (newStyle) {
      node.setAttribute('style', newStyle)
    } else {
      node.removeAttribute('style')
    }
  }

  DOMPurify.addHook('uponSanitizeElement', replaceAnchorWithSpan)
  DOMPurify.addHook('uponSanitizeElement', allowOnlyYoutubeIframe)
  DOMPurify.addHook('uponSanitizeElement', cleanDivStyle)
  DOMPurify.addHook('uponSanitizeElement', removeEmptySrcImg)
  DOMPurify.addHook('uponSanitizeElement', replacePictureWithImg)
  if (isCleanup) {
    DOMPurify.addHook('uponSanitizeElement', removeNonCdnImg)
  }
  DOMPurify.addHook('uponSanitizeAttribute', normalizeProtocolRelativeImgSrc)
  DOMPurify.addHook('uponSanitizeAttribute', allowOnlyHttpsUri)
  try {
    return DOMPurify.sanitize(trimmed, {
      ALLOWED_TAGS: SAFE_COPY_ALLOWED_TAGS,
      ALLOWED_ATTR: SAFE_COPY_ALLOWED_ATTR,
      ALLOWED_URI_REGEXP
    }).replaceAll('"', '\'')
  } catch (error) {
    console.error('Error sanitizing HTML:', error)
    return ''
  } finally {
    DOMPurify.removeHook('uponSanitizeElement')
    DOMPurify.removeHook('uponSanitizeAttribute')
  }
}

/**
 * Returns a new Error with message from CAUSE_CODE_MAP and cause { code, error }.
 * @param {string} code - Value from CAUSE_CODE (constants)
 * @param {Error|null} [error=null] - Optional error to set as cause.error
 * @returns {Error}
 */
export const getCustomError = (code, error = null) =>
  new Error(CAUSE_CODE_MAP[code], { cause: { code, error } })

/**
 * Returns { code, message } for a given cause code.
 * @param {string} code - Value from CAUSE_CODE (constants)
 * @returns {{ code: string, message: string }}
 */
export const getCauseError = (code) => ({ code, message: CAUSE_CODE_MAP[code] })

/**
 * Computes the value for a single schema field (may return a Promise).
 * @param {Document|Element} doc - Parsed document or root element
 * @param {string} html - Raw HTML string
 * @param {Object} fieldSchema - Field config (type, selector, attribute, callback, children)
 * @param {string} path - Output path key
 * @returns {*} Resolved value or Promise
 */
const getFieldValue = (doc, html, fieldSchema, path) => {
  const selector = _.get(fieldSchema, 'selector')
  const type = _.get(fieldSchema, 'type', 'string')
  const attribute = _.get(fieldSchema, 'attribute', 'textContent')
  const callback = _.get(fieldSchema, 'callback', (v) => v)
  const children = _.get(fieldSchema, 'children')

  switch (type) {
    case 'number': {
      const el = selector ? doc.querySelector(selector) : doc
      if (!el) return 0
      const n = _.toNumber(_.trim(_.get(el, attribute, '')))
      return Number.isNaN(n) ? 0 : n
    }

    case 'array': {
      if (!selector || !children || typeof children !== 'object') return []
      const nodes = doc.querySelectorAll(selector)
      const itemPromises = _.map(Array.from(nodes), (node) =>
        getDataFromHtml(html, children, node)
      )
      return Promise.all(itemPromises)
    }

    case 'custom': {
      const el = selector ? doc.querySelector(selector) : doc
      const raw = callback(el ? _.trim(_.get(el, attribute, '')) : '')
      return Promise.resolve(raw)
    }

    case 'grabHtml': {
      const raw = callback(html)
      return Promise.resolve(raw)
    }

    default: {
      const el = selector ? doc.querySelector(selector) : doc
      return el ? _.trim(_.get(el, attribute, '')) : ''
    }
  }
}

/**
 * Parses an HTML string with a selector schema and returns a nested object.
 * Supports async callbacks; resolves all top-level fields in parallel via Promise.all.
 * Uses optional third arg `root` (Document|Element) for recursion on array items.
 * @param {string} html - HTML string
 * @param {Object} schema - Map of output path -> { type: 'string'|'number'|'array'|'custom'|'grabHtml', selector, children? }
 * @param {Document|Element|null} [root] - When set, used as query root instead of parsing html
 * @returns {Promise<Object>}
 */
export const getDataFromHtml = async (html = '', schema = {}, root = null) => {
  const doc = root || new DOMParser().parseFromString(html || '', 'text/html')
  const entries = _.toPairs(schema)

  const valuePromises = entries.map(([path, fieldSchema]) => {
    const value = getFieldValue(doc, html, fieldSchema, path)
    return Promise.resolve(value)
  })

  const values = await Promise.all(valuePromises)
  const flat = _.fromPairs(_.zip(_.map(entries, ([path]) => path), values))
  return buildNestedResult(flat)
}

/**
 * Builds a nested result from flat path -> value. Dot-notation paths (e.g. 'product.copy') become nested keys (result.product.copy).
 * @param {Object} flat - Flat map of path string -> value
 * @returns {Object} Nested result
 */
const buildNestedResult = (flat) => {
  return _.reduce(flat, (result, value, path) => {
    if (path.includes('.')) {
      _.set(result, path, value)
    } else {
      result[path] = value
    }
    return result
  }, {})
}

/**
 * Category attribute `values` for a spec dimension name (same keying as matchAttribute).
 * @param {Array<{ name: string, values?: Array }>} categoryAttributes
 * @param {string} specName
 * @returns {string[]}
 */
const getCategoryValuesForSpecName = (categoryAttributes, specName) => {
  if (_.isEmpty(categoryAttributes) || !specName) {
    return []
  }
  const attrsByName = _.keyBy(categoryAttributes, 'name')
  const attr = _.get(attrsByName, specName, {})
  if (_.isEmpty(attr)) {
    return []
  }
  const valuesArr = _.get(attr, 'values', [])
  return (Array.isArray(valuesArr) ? valuesArr : [])
}

const normalizeCategoryValueEntry = (entry) => {
  if (entry == null) {
    return ''
  }
  if (typeof entry === 'string') {
    return entry
  }
  return _.get(entry, 'name', '') || _.get(entry, 'label', '') || String(entry)
}

/** For unmatched specs: first category list entry for originalSpecName when present, else the scraped option value. */
const getUnmatchCategoryDisplayValue = (categoryAttributes, originalSpecName, originalSpecValue) => {
  const values = getCategoryValuesForSpecName(categoryAttributes, originalSpecName)
  const first = normalizeCategoryValueEntry(_.get(values, 0))
  if (first !== '') {
    return first
  }
  return originalSpecValue
}

/** Unmatched axis: scraped value as displayName; spec.values from category first entry or scraped default. */
const buildSpecAxisEntryForIndex = (
  categoryAttributes,
  specName,
  originalSpecValue,
  isUnmatch
) => ({
  ...(isUnmatch && { displayName: originalSpecValue }),
  spec: {
    name: specName,
    values: isUnmatch
      ? [getUnmatchCategoryDisplayValue(categoryAttributes, specName, originalSpecValue)]
      : [originalSpecValue]
  }
})

/**
 * Returns models array (or null) and max size from form values and base payload for listing clone.
 * productSpecs from basePayload is [{ name, values, isUnmatch? }] with length 0~2; productImages is an array of image URLs.
 * maxSize is the maximum string length among model.spec.values[0] and, when present, each model.items[].spec.values[0].
 * When a spec has isUnmatch true: spec.name stays the payload axis; spec.values use the first entry of that dimension in categoryAttributes (same name key), or the scraped option for that row if none. First-dimension models use the scraped option value as displayName; second-dimension items use scraped option values as displayName.
 * @param {Object} formValues - Form values; productAttribute 'none' yields null
 * @param {Object} basePayload - Has productSpecs and productImages
 * @param {Array<{ name: string, values?: Array, constraints?: Object }>} [categoryAttributes=[]] - Category cluster attributes when specs are unmatched
 * @returns {{ models: Array|null, maxSize: number }}
 */
const EMPTY_MODELS = { models: null, maxSize: 0 }
export const getModelsAndMaxSize = (formValues, basePayload, categoryAttributes = []) => {
  const specs = getSpecsFromPayload(basePayload)
  const productImages = _.get(basePayload, 'productImages', [])
    .filter(image => image !== '#' && !_.isEmpty(image))
    .map((image, order) => ({ url: image, order: order + 1 }))
  const modelsProductImages = _.size(productImages) > 1 ? productImages : []
  const productAttribute = _.get(formValues, FIELD.PRODUCT_ATTRIBUTE)
  const isNoneSpec = productAttribute === 'none'
  const isAutoSpec = productAttribute === 'auto'
  const specSize = _.size(specs)
  const isSpecEmpty = specSize === 0
  const isEmptyProductImage = _.size(modelsProductImages) === 0

  if (isSpecEmpty && isEmptyProductImage) {
    return EMPTY_MODELS
  }

  const [firstSpecName = '', secondSpecName = ''] = specs.map(spec => spec.name)
  const firstSpec = specs[0]
  const firstSpecValuesRaw = _.get(firstSpec, 'values', [])
  const firstSpecValues = Array.isArray(firstSpecValuesRaw) ? firstSpecValuesRaw : []

  if (isNoneSpec || (isAutoSpec && isSpecEmpty)) {
    const models = [{ images: modelsProductImages }]
    const maxSize = 0
    return { models, maxSize }
  }

  if (specSize === 1) {
    const firstSpecIsUnmatch = !!_.get(firstSpec, 'isUnmatch', false)
    const models = firstSpecValues.map((firstSpecValue) => ({
      ...buildSpecAxisEntryForIndex(
        categoryAttributes,
        firstSpecName,
        firstSpecValue,
        firstSpecIsUnmatch
      ),
      images: modelsProductImages
    }))
    const maxSize = models.length === 0
      ? 0
      : Math.max(...models.map(m => String(_.get(m, 'spec.values[0]', '')).length))
    return { models, maxSize }
  }

  const secondSpec = specs[1]
  const secondSpecValuesRaw = _.get(secondSpec, 'values', [])
  const secondSpecValues = Array.isArray(secondSpecValuesRaw) ? secondSpecValuesRaw : []
  const firstSpecIsUnmatch = !!_.get(firstSpec, 'isUnmatch', false)
  const secondSpecIsUnmatch = !!_.get(secondSpec, 'isUnmatch', false)
  const itemTemplates = secondSpecValues.map((secondSpecValue) =>
    buildSpecAxisEntryForIndex(
      categoryAttributes,
      secondSpecName,
      secondSpecValue,
      secondSpecIsUnmatch
    )
  )

  const models = firstSpecValues.map((firstSpecValue) => ({
    ...buildSpecAxisEntryForIndex(
      categoryAttributes,
      firstSpecName,
      firstSpecValue,
      firstSpecIsUnmatch
    ),
    images: modelsProductImages,
    items: itemTemplates
  }))

  let maxSize = 0
  for (const model of models) {
    const mainLabelLength = String(_.get(model, 'spec.values[0]', '')).length
    const items = _.get(model, 'items', [])
    for (const item of items) {
      const itemLabelLength = String(_.get(item, 'spec.values[0]', '')).length
      maxSize = Math.max(maxSize, mainLabelLength + itemLabelLength)
    }
  }
  return { models, maxSize }
}

const getNormalizedTitle = (title = '') => {
  return title
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/^-/g, '')
    .replace(/[<>\n]/g, '')
    .trim()
}

/** Invisible/format characters to strip from strings (zero-width, BOM, soft hyphen, etc.) */
// eslint-disable-next-line no-misleading-character-class -- intentional: strip invisible/format chars
const INVISIBLE_CHAR_REGEX = /[\u200B-\u200D\uFEFF\u00AD\u2060\u180E]/g

/** Platform value -> phrases to remove from payload string fields (platform-specific wording) */
const PLATFORM_WORDING_MAP = {
  shopee: ['Shopee', 'shopee', 'SHOPEE', '蝦皮'],
  momo: ['momo', 'Momo', 'MOMO']
}

const removeInvisibleChars = (str) => {
  if (typeof str !== 'string') {
    return str
  }

  return str.replace(INVISIBLE_CHAR_REGEX, '')
}

const removePlatformWording = (str, platform) => {
  if (typeof str !== 'string' || !platform) {
    return str
  }

  const phrases = PLATFORM_WORDING_MAP[platform]
  if (!Array.isArray(phrases) || phrases.length === 0) {
    return str
  }

  let result = str
  for (const phrase of phrases) {
    if (phrase) {
      const re = new RegExp(_.escapeRegExp(phrase), 'g')
      result = result.replace(re, '')
    }
  }
  return result
}

/**
 * Recursively cleans payload: removes platform-specific wording and invisible chars from all string fields.
 * @param {*} value - Any value (object, array, string, etc.)
 * @param {string} platform - formValues.platform ('shopee' | 'momo')
 * @returns {*} Cleaned clone
 */
export const cleanPayloadStrings = (value, platform) => {
  if (typeof value === 'string') {
    return removeInvisibleChars(removePlatformWording(value, platform))
  }

  if (Array.isArray(value)) {
    return value.map(item => cleanPayloadStrings(item, platform))
  }

  if (value != null && typeof value === 'object') {
    return _.mapValues(value, v => cleanPayloadStrings(v, platform))
  }

  return value
}

// Backend Java validates applicant with \p{IsHan}{0,10}; JS uses Script=Han (IsHan is invalid in RegExp).
const HAN_APPLICANT_PATTERN = /^\p{Script=Han}{0,10}$/u
const DEFAULT_APPLICANT = '雅虎'

const resolveApplicant = () => {
  const raw = _.get(global.window, 'ISO_REDUX_DATA.page.user.identity.name', DEFAULT_APPLICANT)
  const trimmed = String(raw).trim()
  return HAN_APPLICANT_PATTERN.test(trimmed) ? trimmed : DEFAULT_APPLICANT
}

const ATTRIBUTE_MAX_LENGTH = 30
const ATTRIBUTE_VALUE_MAX_LENGTH = 50

export const getSpecsFromPayload = (basePayload) => {
  const productSpecs = _.get(basePayload, 'productSpecs', [])
  const specs = Array.isArray(productSpecs) ? productSpecs.slice(0, 2) : []
  return specs.map(spec => ({
    ...spec,
    name: (_.get(spec, 'name', '') || '').slice(0, ATTRIBUTE_MAX_LENGTH).trim(),
    values: (Array.isArray(spec.values) ? spec.values : []).map(v => v.replace(/[<>]/g, ''))
  }))
}

const getSpecNameSet = (basePayload) => {
  return new Set(getSpecsFromPayload(basePayload).map(spec => spec.name).filter(Boolean))
}

/**
 * Returns the create-proposal payload from form values and optional existing proposal.
 * @param {Object} formValues - Form values (e.g. urlFormValues); may include subStationId, product.struDataAttrClusterId (see FIELD), FIELD.EXTERNAL_URL for crossPlatformUrl
 * @param {Object|null} existingProposal - Existing proposal from store; payload is merged (after omitting server-only fields)
 * @param {Array<{ name: string, values?: Array, constraints?: Object }>} [categoryAttributes=[]] - Category cluster attributes when a spec has isUnmatch (first value per dimension for model spec.values; displayName holds scraped option; outgoing product.productSpecs are stripped to name/values only)
 * @returns {Object} Payload for createProposals (includes expiredTs); includes crossPlatformUrl when FIELD.EXTERNAL_URL is non-empty (raw URL preserved, no platform wording strip on host)
 */
export const getCreateProposalPayload = (inputFormValues = {}, inputProposal = null, categoryAttributes = []) => {
  const crossPlatformUrl = _.trim(_.get(inputFormValues, FIELD.EXTERNAL_URL, '') || '')
  const platform = _.get(inputFormValues, FIELD.PLATFORM, '')
  const formValues = cleanPayloadStrings(inputFormValues, platform)
  const existingProposal = cleanPayloadStrings(inputProposal, platform)
  const basePayload = existingProposal || {}
  const categoryAttrs = Array.isArray(categoryAttributes) ? categoryAttributes : []
  const subStationId = _.get(formValues, FIELD.SUB_STATION_ID)
  const catItemId = _.get(formValues, 'product.catItemId')
  const struDataAttrClusterId = _.get(formValues, FIELD.CATEGORY)
  const title = getNormalizedTitle(_.get(basePayload, 'title', ''))
  const { models, maxSize: maxSizeProductSpecs } = getModelsAndMaxSize(formValues, basePayload, categoryAttrs)
  const specNameSet = getSpecNameSet(basePayload)
  const attributes = _.get(basePayload, 'attributesTable', [])
    .filter(attribute => {
      const label = _.get(attribute, 'label', '')
      const value = _.get(attribute, 'value', '')
      return !_.isEmpty(label) && !_.isEmpty(value)
    })
    .map(attribute => {
      const label = _.get(attribute, 'label', '').slice(0, ATTRIBUTE_MAX_LENGTH).trim()
      const value = _.get(attribute, 'value', '').slice(0, ATTRIBUTE_VALUE_MAX_LENGTH).trim()
      return { name: label, values: [value] }
    })
    .filter(attribute => !specNameSet.has(_.get(attribute, 'name')))
  const dynamicListing = {
    ...(!_.isUndefined(catItemId) && { catItemId })
  }
  const brandId = _.get(basePayload, 'brand.id', DEFAULT_BRAND_ID)
  const baseProduct = _.get(basePayload, 'product', {}) || {}
  const sanitizedProductSpecs = Array.isArray(baseProduct.productSpecs) && !_.isEmpty(baseProduct.productSpecs)
    ? getSpecsFromPayload({ productSpecs: baseProduct.productSpecs }).map(s => ({
      name: s.name,
      values: s.values
    }))
    : null
  const dynamicProudct = {
    ...baseProduct,
    ...(sanitizedProductSpecs != null && { productSpecs: sanitizedProductSpecs }),
    ...(!_.isUndefined(struDataAttrClusterId) && { struDataAttrClusterId }),
    ...(!_.isUndefined(catItemId) && { catItemId }),
    ...(!_.isEmpty(attributes) && { attributes }),
    ...(!_.isEmpty(models) && { models })
  }
  const dynamicPayload = {
    ...(!_.isUndefined(subStationId) && { subStationId })
  }
  const maximumTitle = title.slice(0, 100 - 2 - maxSizeProductSpecs)
  const originalShortDescription = _.get(basePayload, 'shortDescription')
  const shortDescription = (
    _.isEmpty(originalShortDescription) ? [title] : originalShortDescription
  ).map(desc => stripNonCjkAlphanumeric(desc).slice(0, 15).trim())
  const payload = {
    applicant: resolveApplicant(),
    type: PROPOSAL_TYPE.NEW_LISTING,
    reviewStatus: PROPOSAL_REVIEW_STATUS.COMPOSING,
    expiredTs: moment().add(10, 'days').format(),
    listing: {
      ...dynamicListing,
      seoUrl: stripNonCjkAlphanumeric(maximumTitle.replace(/\s+/g, '-')).slice(0, 50).trim()
    },
    product: {
      ...dynamicProudct,
      name: maximumTitle,
      brandId,
      copy: getSafeCopy(dynamicProudct.copy, true),
      attributeDisplayMode: PRODUCT_ATTR_DISPLAY_MODE.TABLE,
      shortDescription,
      shipType: {
        id: PRODUCT_SHIP_TYPE.HOME
      }
    },
    ...dynamicPayload
  }
  return {
    ...cleanPayloadStrings(payload, platform),
    crossPlatformUrl
  }
}

/**
 * Extracts proposal field paths from create-proposal error payload.
 * Reads payload.errorData.error[0].detail and maps each item's invalidValue
 * (before ': ') to a field path string.
 * @param {Object} error - Error or response object with payload.errorData.error[0].detail
 * @returns {string[]} Non-empty field path strings (e.g. ['proposal.product.seoUrl', ...])
 */
export const getErrorDetailFields = (error) => {
  const errorDetails = _.get(error, 'payload.errorData.error[0].detail', [])
  return _.map(errorDetails, errorDetail => {
    const invalidValue = _.get(errorDetail, 'invalidValue', '')
    const [field, value] = invalidValue.split(': ')
    // 如果 field 或 value 為空代表非 field 的錯誤，直接回傳空字串
    if (_.isEmpty(field) || _.isEmpty(value)) {
      return ''
    }
    return field
  }).filter(field => !_.isEmpty(field))
}

/**
 * Converts path string to lodash-friendly form (bracket notation to dot notation).
 * @param {string} path - e.g. 'proposal.product.attributes[2].values'
 * @returns {string} e.g. 'proposal.product.attributes.2.values'
 */
const toLodashPath = (path) => {
  if (typeof path !== 'string') return ''
  return path.replace(/\[\d+\]/g, (match) => '.' + match.slice(1, -1))
}

/**
 * Normalizes error detail field paths for removal. Both
 * proposal.product.attributes[x].values and proposal.product.attributes[x]
 * are normalized to proposal.product.attributes[x] (lodash path form).
 * @param {string[]} fields - Array of path strings from API error details
 * @returns {string[]} Unique array of lodash-style paths to remove
 */
export const normalizeErrorDetailPaths = (fields) => {
  if (!Array.isArray(fields) || _.isEmpty(fields)) return []
  const normalized = _.map(fields, (field) => {
    const path = toLodashPath(_.trim(field))
    if (!path) return null
    const attrMatch = path.match(/^proposal\.product\.attributes\.(\d+)(\..*)?$/)
    if (attrMatch) {
      return `proposal.product.attributes.${attrMatch[1]}`
    }
    return path
  })
  return _.uniq(_.filter(normalized, Boolean))
}

/**
 * Returns a deep clone of proposal with the given paths removed.
 * Paths must be in lodash-friendly form (e.g. proposal.product.attributes.2).
 * When a path targets an array index (e.g. attributes.1), that element is spliced
 * out so the array length decreases; other paths are removed via _.unset.
 * @param {Object|null} proposal - Proposal object
 * @param {string[]} paths - Paths to remove
 * @returns {Object|null} New proposal with paths removed
 */
export const removeProposalFields = (proposal, paths) => {
  if (proposal == null) return proposal
  if (!Array.isArray(paths) || _.isEmpty(paths)) return _.cloneDeep(proposal)
  const clone = _.cloneDeep(proposal)
  _.forEach(paths, (path) => {
    const arrayIndexMatch = path.match(/^(.+)\.(\d+)$/)
    if (arrayIndexMatch) {
      const parentPath = arrayIndexMatch[1]
      const index = parseInt(arrayIndexMatch[2], 10)
      const parent = _.get(clone, parentPath)
      if (Array.isArray(parent)) {
        parent.splice(index, 1)
      } else {
        _.unset(clone, path)
      }
    } else {
      _.unset(clone, path)
    }
  })
  return clone
}

// Matches https:// and protocol-relative (//) image URLs
const HTTPS_IMAGE_LINK_REGEX = /https:\/\/[^\s"']+\.(?:png|jpe?g|gif|webp)(?:\?[^\s"']*)?(?:#[^\s"']*)?/gi

/**
 * Extracts all https image links from JSON-serializable data.
 * Uses stringify + regex; returns unique array of URLs (image extensions: png, jpeg, jpg, gif, webp).
 * @param {*} data - JSON-serializable data
 * @returns {string[]}
 */
export const getHttpsImageLinksFromData = (data) => {
  if (data == null) {
    return []
  }
  const str = JSON.stringify(data)
  const matches = str.match(HTTPS_IMAGE_LINK_REGEX) || []
  // Normalize protocol-relative URLs (//...) to https so they are fetchable
  return _.uniq(matches)
}

/**
 * Replaces origin image links with S3 links in data using a mapping.
 * Does not mutate input; returns new object. Replaces longest origin URLs first.
 * When data has productImages, sorts productImages by mapping[url].dimensions.width (desc) before replace.
 * @param {*} data - JSON-serializable data
 * @param {Record<string, { url: string, dimensions?: { width?: number } }>} mapping - originUrl -> { url: replacementUrl, dimensions? } for replacement and optional sort by width
 * @returns {*}
 */
export const replaceImageLinksWithMapping = (data, mapping) => {
  const replacedData = _.cloneDeep(data)
  if (!mapping || typeof mapping !== 'object' || _.isEmpty(mapping)) {
    return replacedData
  }

  const productImages = _.get(replacedData, 'productImages', [])
  if (!_.isEmpty(productImages) && !_.isEmpty(mapping)) {
    replacedData.productImages = _.orderBy(
      productImages,
      (url) => _.get(mapping, [url, 'dimensions', 'width'], 0),
      ['desc']
    )
  }
  const replaceMap = Object.fromEntries(
    Object.keys(mapping).map((originUrl) => [
      originUrl,
      _.get(mapping, [originUrl, 'url'], '')
    ])
  )
  // Sort by origin URL length descending so longer URLs are replaced first; otherwise a shorter
  // origin that is a prefix of a longer one would be replaced inside the longer URL and corrupt it.
  const entries = Object.entries(replaceMap).sort((a, b) => b[0].length - a[0].length)
  let str = JSON.stringify(replacedData)
  for (const [origin, s3] of entries) {
    str = str.split(origin).join(s3)
  }
  return JSON.parse(str)
}

/**
 * Returns proposal with extra product image URL appended to productImages when given.
 * Pure: does not mutate input. If extraProductImageUrl is falsy, returns proposal as-is (shallow clone).
 * @param {Object} proposal - Proposal object (may have productImages array)
 * @param {string|null|undefined} extraProductImageUrl - URL to append when there is exactly one product image
 * @returns {Object}
 */
export const proposalWithExtraProductImageUrl = (proposal, extraProductImageUrl) => {
  if (!extraProductImageUrl) {
    return _.cloneDeep(proposal)
  }
  return {
    ...proposal,
    [SCHEMA.PRODUCT_IMAGES]: [
      ..._.get(proposal, SCHEMA.PRODUCT_IMAGES, []),
      extraProductImageUrl
    ]
  }
}
